import { Minus, Plus, ShoppingBag, X } from "lucide-react";
import { CONTACT_INFO } from "../data";
import { formatPrice } from "../format";
import { useCatalogI18n } from "../i18n";
import { BG, BLUE, BORDER, GOLD, MUTED, WHITE } from "../theme";
import type { CartEntry, Dessert, DessertSet } from "../types";

type DessertDetailModalProps = {
  selectedDessert: Dessert | null;
  quantity: number;
  onDecreaseQty: () => void;
  onIncreaseQty: () => void;
  onClose: () => void;
  onAddToCart: () => void;
};

export function DessertDetailModal({
  selectedDessert,
  quantity,
  onDecreaseQty,
  onIncreaseQty,
  onClose,
  onAddToCart
}: DessertDetailModalProps) {
  const { copy } = useCatalogI18n();

  if (!selectedDessert) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center p-4 backdrop-blur-sm"
      style={{ backgroundColor: "rgba(9, 71, 131, 0.35)" }}
      onClick={onClose}
    >
      <div
        className="rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-hidden grid md:grid-cols-2 relative"
        style={{ backgroundColor: WHITE }}
        onClick={(event) => event.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full flex items-center justify-center shadow-sm"
          style={{ backgroundColor: "rgba(255,255,255,0.95)", color: BLUE }}
        >
          <X className="w-5 h-5" />
        </button>
        <div className="aspect-square md:aspect-auto" style={{ backgroundColor: BG }}>
          <img
            src={selectedDessert.image}
            alt={selectedDessert.name}
            className="w-full h-full object-cover"
          />
        </div>
        <div className="p-8 md:p-10 flex flex-col overflow-y-auto">
          <p
            className="text-xs uppercase mb-3"
            style={{ color: GOLD, letterSpacing: "0.25em", fontWeight: 600 }}
          >
            {selectedDessert.category}
          </p>
          <h2
            className="text-3xl md:text-4xl mb-4 leading-tight"
            style={{ color: BLUE, fontWeight: 600, letterSpacing: "-0.01em" }}
          >
            {selectedDessert.name}
          </h2>
          <p className="text-2xl mb-6" style={{ color: BLUE, fontWeight: 700 }}>
            {formatPrice(selectedDessert.price)}
          </p>
          <p className="leading-relaxed mb-8" style={{ color: MUTED }}>
            {selectedDessert.description}
          </p>

          <div className="mt-auto">
            <p
              className="text-xs uppercase mb-3"
              style={{ color: GOLD, letterSpacing: "0.25em", fontWeight: 600 }}
            >
              {copy.overlays.quantityLabel}
            </p>
            <div className="flex items-center gap-4 mb-6">
              <button
                onClick={onDecreaseQty}
                className="w-10 h-10 rounded-full border flex items-center justify-center transition-colors"
                style={{ borderColor: BORDER, color: BLUE }}
              >
                <Minus className="w-4 h-4" />
              </button>
              <span className="text-xl w-8 text-center" style={{ color: BLUE, fontWeight: 600 }}>
                {quantity}
              </span>
              <button
                onClick={onIncreaseQty}
                className="w-10 h-10 rounded-full border flex items-center justify-center transition-colors"
                style={{ borderColor: BORDER, color: BLUE }}
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
            <button
              onClick={onAddToCart}
              className="w-full py-4 rounded-full text-sm uppercase transition-colors"
              style={{
                backgroundColor: BLUE,
                color: WHITE,
                letterSpacing: "0.25em",
                fontWeight: 600
              }}
              onMouseEnter={(event) => (event.currentTarget.style.backgroundColor = GOLD)}
              onMouseLeave={(event) => (event.currentTarget.style.backgroundColor = BLUE)}
            >
              {copy.overlays.addToCartButton} · {formatPrice(selectedDessert.price * quantity)}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

type SetDetailModalProps = {
  selectedSet: DessertSet | null;
  quantity: number;
  onDecreaseQty: () => void;
  onIncreaseQty: () => void;
  onClose: () => void;
  onAddToCart: () => void;
};

export function SetDetailModal({
  selectedSet,
  quantity,
  onDecreaseQty,
  onIncreaseQty,
  onClose,
  onAddToCart
}: SetDetailModalProps) {
  const { copy } = useCatalogI18n();

  if (!selectedSet) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center p-4 backdrop-blur-sm"
      style={{ backgroundColor: "rgba(9, 71, 131, 0.35)" }}
      onClick={onClose}
    >
      <div
        className="rounded-3xl max-w-5xl w-full max-h-[92vh] overflow-hidden relative flex flex-col"
        style={{ backgroundColor: WHITE }}
        onClick={(event) => event.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full flex items-center justify-center shadow-sm"
          style={{ backgroundColor: "rgba(255,255,255,0.95)", color: BLUE }}
        >
          <X className="w-5 h-5" />
        </button>

        <div className="overflow-y-auto">
          <div className="relative overflow-hidden" style={{ backgroundColor: BG, aspectRatio: "21 / 9" }}>
            <img src={selectedSet.mainImage} alt={selectedSet.name} className="w-full h-full object-cover" />
            <div
              className="absolute bottom-0 left-0 right-0 h-1/2"
              style={{
                background: "linear-gradient(to top, rgba(9,71,131,0.55), transparent)"
              }}
            />
            <div className="absolute bottom-6 left-6 md:bottom-8 md:left-10 right-6">
              {selectedSet.tagline && (
                <p
                  className="text-xs uppercase mb-2"
                  style={{ color: GOLD, letterSpacing: "0.3em", fontWeight: 600 }}
                >
                  {selectedSet.tagline}
                </p>
              )}
              <h2
                className="text-3xl md:text-5xl leading-tight"
                style={{
                  color: WHITE,
                  fontWeight: 700,
                  letterSpacing: "-0.01em",
                  textShadow: "0 2px 20px rgba(9,71,131,0.4)"
                }}
              >
                {selectedSet.name}
              </h2>
            </div>
          </div>

          <div className="p-8 md:p-10">
            <p className="leading-relaxed mb-8 text-base" style={{ color: MUTED }}>
              {selectedSet.description}
            </p>

            <p
              className="text-xs uppercase mb-5"
              style={{ color: GOLD, letterSpacing: "0.25em", fontWeight: 600 }}
            >
              {copy.overlays.whatsInsideLabel}
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              {selectedSet.items.map((item, index) => (
                <div key={index} className="rounded-xl overflow-hidden" style={{ backgroundColor: BG }}>
                  <div className="aspect-square overflow-hidden">
                    <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="p-3">
                    <h4 className="text-sm mb-0.5 leading-tight" style={{ color: BLUE, fontWeight: 600 }}>
                      {item.name}
                    </h4>
                    <p className="text-xs mb-1.5 leading-relaxed" style={{ color: MUTED }}>
                      {item.description}
                    </p>
                    <span
                      className="inline-block text-xs px-2 py-0.5 rounded-full"
                      style={{
                        backgroundColor: WHITE,
                        color: GOLD,
                        fontWeight: 700,
                        letterSpacing: "0.05em"
                      }}
                    >
                      ×{item.quantity} {copy.overlays.pieceUnit}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            <div
              className="flex flex-col md:flex-row md:items-end gap-6 pt-6 border-t"
              style={{ borderColor: BORDER }}
            >
              <div>
                <p
                  className="text-xs uppercase mb-2"
                  style={{ color: GOLD, letterSpacing: "0.25em", fontWeight: 600 }}
                >
                  {copy.overlays.setPriceLabel}
                </p>
                <p className="text-4xl" style={{ color: BLUE, fontWeight: 700, letterSpacing: "-0.02em" }}>
                  {formatPrice(selectedSet.price)}
                </p>
              </div>

              <div>
                <p
                  className="text-xs uppercase mb-2"
                  style={{ color: GOLD, letterSpacing: "0.25em", fontWeight: 600 }}
                >
                  {copy.overlays.quantityLabel}
                </p>
                <div className="flex items-center gap-4">
                  <button
                    onClick={onDecreaseQty}
                    className="w-10 h-10 rounded-full border flex items-center justify-center"
                    style={{ borderColor: BORDER, color: BLUE }}
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="text-xl w-8 text-center" style={{ color: BLUE, fontWeight: 600 }}>
                    {quantity}
                  </span>
                  <button
                    onClick={onIncreaseQty}
                    className="w-10 h-10 rounded-full border flex items-center justify-center"
                    style={{ borderColor: BORDER, color: BLUE }}
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <button
                onClick={onAddToCart}
                className="flex-1 md:ml-auto py-4 px-6 rounded-full text-sm uppercase transition-colors"
                style={{
                  backgroundColor: BLUE,
                  color: WHITE,
                  letterSpacing: "0.25em",
                  fontWeight: 600
                }}
                onMouseEnter={(event) => (event.currentTarget.style.backgroundColor = GOLD)}
                onMouseLeave={(event) => (event.currentTarget.style.backgroundColor = BLUE)}
              >
                {copy.overlays.addToCartButton} · {formatPrice(selectedSet.price * quantity)}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

type CartDrawerProps = {
  open: boolean;
  cart: CartEntry[];
  cartTotal: number;
  onClose: () => void;
  onUpdateQty: (id: number, delta: number) => void;
  onRemoveItem: (id: number) => void;
};

export function CartDrawer({
  open,
  cart,
  cartTotal,
  onClose,
  onUpdateQty,
  onRemoveItem
}: CartDrawerProps) {
  const { copy } = useCatalogI18n();

  // Build an itemized order from the cart and hand it off to WhatsApp. The
  // number is taken from the contact details (digits only, as wa.me expects).
  const handleCheckout = () => {
    const lines = cart.map((item, index) => {
      const label = item.isSet ? `${item.name} (${copy.overlays.setBadge})` : item.name;
      return `${index + 1}. ${label} × ${item.quantity} — ${formatPrice(item.price * item.quantity)}`;
    });
    const message = [
      copy.overlays.orderIntro,
      "",
      ...lines,
      "",
      `${copy.overlays.totalLabel}: ${formatPrice(cartTotal)}`
    ].join("\n");
    const phone = CONTACT_INFO.phone.replace(/\D/g, "");
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(message)}`, "_blank");
  };

  if (!open) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-40 backdrop-blur-sm"
      style={{ backgroundColor: "rgba(9, 71, 131, 0.35)" }}
      onClick={onClose}
    >
      <div
        className="absolute right-0 top-0 bottom-0 w-full max-w-md flex flex-col"
        style={{ backgroundColor: WHITE }}
        onClick={(event) => event.stopPropagation()}
      >
        <div
          className="flex items-center justify-between p-6 border-b"
          style={{ borderColor: BORDER }}
        >
          <h3 className="text-2xl" style={{ color: BLUE, fontWeight: 600, letterSpacing: "-0.01em" }}>
            {copy.overlays.yourCartTitle}
          </h3>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-full flex items-center justify-center transition-colors"
            style={{ color: BLUE }}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {cart.length === 0 ? (
          <div className="flex-1 flex items-center justify-center p-6">
            <div className="text-center">
              <ShoppingBag className="w-12 h-12 mx-auto mb-4" style={{ color: GOLD }} />
              <p className="text-lg" style={{ color: MUTED, fontWeight: 300 }}>
                {copy.overlays.cartEmptyText}
              </p>
            </div>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto p-6 space-y-5">
              {cart.map((item) => (
                <div
                  key={item.id}
                  className="flex gap-4 pb-5 border-b"
                  style={{ borderColor: BORDER }}
                >
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-20 h-20 rounded-xl object-cover flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      {item.isSet && (
                        <span
                          className="text-[10px] uppercase px-1.5 py-0.5 rounded"
                          style={{
                            backgroundColor: GOLD,
                            color: WHITE,
                            letterSpacing: "0.1em",
                            fontWeight: 700
                          }}
                        >
                          {copy.overlays.setBadge}
                        </span>
                      )}
                    </div>
                    <h4 className="text-base mb-1" style={{ color: BLUE, fontWeight: 600 }}>
                      {item.name}
                    </h4>
                    <p className="text-sm mb-3" style={{ color: MUTED }}>
                      {formatPrice(item.price)}
                    </p>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => onUpdateQty(item.id, -1)}
                        className="w-7 h-7 rounded-full border flex items-center justify-center"
                        style={{ borderColor: BORDER, color: BLUE }}
                      >
                        <Minus className="w-3 h-3" />
                      </button>
                      <span className="text-sm w-6 text-center" style={{ color: BLUE, fontWeight: 600 }}>
                        {item.quantity}
                      </span>
                      <button
                        onClick={() => onUpdateQty(item.id, 1)}
                        className="w-7 h-7 rounded-full border flex items-center justify-center"
                        style={{ borderColor: BORDER, color: BLUE }}
                      >
                        <Plus className="w-3 h-3" />
                      </button>
                      <button
                        onClick={() => onRemoveItem(item.id)}
                        className="ml-auto text-xs uppercase"
                        style={{ color: GOLD, letterSpacing: "0.15em", fontWeight: 600 }}
                      >
                        {copy.overlays.removeButton}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="p-6 border-t" style={{ borderColor: BORDER }}>
              <div className="flex justify-between items-baseline mb-6">
                <span
                  className="text-sm uppercase"
                  style={{ color: MUTED, letterSpacing: "0.25em", fontWeight: 500 }}
                >
                  {copy.overlays.totalLabel}
                </span>
                <span className="text-2xl" style={{ color: BLUE, fontWeight: 700 }}>
                  {formatPrice(cartTotal)}
                </span>
              </div>
              <button
                onClick={handleCheckout}
                className="w-full py-4 rounded-full text-sm uppercase transition-colors"
                style={{
                  backgroundColor: GOLD,
                  color: WHITE,
                  letterSpacing: "0.25em",
                  fontWeight: 600
                }}
                onMouseEnter={(event) => (event.currentTarget.style.backgroundColor = BLUE)}
                onMouseLeave={(event) => (event.currentTarget.style.backgroundColor = GOLD)}
              >
                {copy.overlays.checkoutButton}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
